import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Brain, Send, Zap, Clock, Coins, AlertCircle, CheckCircle, RefreshCw
} from 'lucide-react';
import { friendlyAiProviderName } from '@/lib/providerLabels';

export default function AITestConsole() {
  const [models, setModels] = useState([]);
  const [limits, setLimits] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const [selectedModel, setSelectedModel] = useState('');
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [mRes, lRes, rRes] = await Promise.all([
          api.get('/tenant/ai/models'),
          api.get('/tenant/ai/limits').catch(() => ({ data: {} })),
          api.get('/tenant/ai/requests?limit=10&request_type=test').catch(() => ({ data: [] })),
        ]);
        const allModels = mRes.data || [];
        const limitsData = lRes.data || {};
        // Filter out premium models if not enabled
        const premiumEnabled = limitsData.ai_premium_models_enabled !== false;
        setModels(premiumEnabled ? allModels : allModels.filter(m => m.quality_tier !== 'premium'));
        setLimits(limitsData);
        setLogs(rRes.data || []);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleTest = async () => {
    if (!prompt.trim()) { toast.error('Escribe un prompt'); return; }
    setTesting(true);
    setResult(null);
    try {
      const body = { prompt: prompt.trim(), environment: 'sandbox' };
      if (selectedModel) body.model_key = selectedModel;
      const { data } = await api.post('/tenant/ai/test', body);
      setResult(data);
      if (data.ok) {
        toast.success(`Respuesta en ${data.latency_ms}ms`);
      } else {
        toast.error(data.error || 'Error en la solicitud');
      }
      // Refresh logs
      const rRes = await api.get('/tenant/ai/requests?limit=10&request_type=test');
      setLogs(rRes.data || []);
    } catch (e) {
      const status = e.response?.status;
      const raw = e.response?.data?.detail;
      const data = e.response?.data;
      // If the AI actually responded ok but billing crashed (CR-017)
      if (data && data.ok === true) {
        setResult({ ...data, billing_recorded: false });
        toast.warning(`Respuesta IA recibida, pero hubo un error al registrar billing.`);
      } else {
        let detail = formatApiError(raw);
        if (status === 500 && !raw) {
          detail = 'Error interno al guardar la respuesta. La IA pudo haber respondido — revisa Tests Recientes.';
        }
        setResult({ ok: false, error: detail, provider_raw_status: status });
        toast.error(detail);
      }
    } finally { setTesting(false); }
  };

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-6" data-testid="ai-test-console">
      <div>
        <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2">
          <Brain className="w-6 h-6" /> Consola IA
        </h1>
        <p className="text-sm text-[#64748B] mt-1">Prueba modelos de IA configurados en tu plan</p>
      </div>

      {/* Limits bar */}
      {limits && (
        <div className="flex gap-4 text-xs" data-testid="ai-limits-bar">
          {limits.max_ai_requests_monthly && (
            <span className="text-[#64748B]">
              Requests: {limits.current_usage?.requests || 0}/{limits.max_ai_requests_monthly}
            </span>
          )}
          {limits.max_ai_tokens_monthly && (
            <span className="text-[#64748B]">
              Tokens: {(limits.current_usage?.tokens || 0).toLocaleString()}/{limits.max_ai_tokens_monthly.toLocaleString()}
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Test panel */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4">
            <div>
              <label className="text-xs font-medium text-[#64748B] mb-1.5 block">Modelo</label>
              <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)}
                className="w-full text-sm border border-[#E2E8F0] rounded-lg px-3 py-2 bg-white text-[#0F172A]"
                data-testid="ai-model-select">
                <option value="">Auto (modelo por defecto)</option>
                {models.map(m => (
                  <option key={m.model_key} value={m.model_key}>
                    {m.display_name} — {friendlyAiProviderName(m.provider_key)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-[#64748B] mb-1.5 block">Prompt</label>
              <Textarea
                value={prompt} onChange={e => setPrompt(e.target.value)}
                placeholder="Escribe tu prompt aquí..."
                rows={4} className="resize-none"
                data-testid="ai-prompt-input"
              />
            </div>

            <Button onClick={handleTest} disabled={testing || !prompt.trim()}
              className="w-full bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="ai-test-btn">
              {testing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {testing ? 'Ejecutando...' : 'Ejecutar Prueba'}
            </Button>
          </div>

          {/* Result */}
          {result && (
            <div className={`bg-white border rounded-xl p-5 ${result.ok ? 'border-emerald-200' : 'border-red-200'}`}
              data-testid="ai-test-result">
              <div className="flex items-center gap-2 mb-3">
                {result.ok ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <AlertCircle className="w-4 h-4 text-red-500" />}
                <span className="text-sm font-semibold text-[#0F172A]">{result.ok ? 'Respuesta' : 'Error'}</span>
                {result.model_key && <span className="text-[10px] bg-[#F1F5F9] px-2 py-0.5 rounded">{result.model_key}</span>}
              </div>

              {result.ok ? (
                <>
                  <div className="bg-[#F8FAFC] rounded-lg p-4 mb-3 text-sm text-[#0F172A] whitespace-pre-wrap" data-testid="ai-response-text">
                    {result.text}
                  </div>
                  <div className="flex flex-wrap gap-4 text-[11px] text-[#94A3B8]">
                    <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {result.total_tokens} tokens</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {result.latency_ms}ms</span>
                    <span className="flex items-center gap-1"><Coins className="w-3 h-3" /> ${result.estimated_cost?.toFixed(6)} {result.currency}</span>
                    {result.fallback_used && <span className="text-amber-500">Fallback usado</span>}
                    {result.billing_recorded === false && <span className="text-amber-500">Billing no registrado</span>}
                  </div>
                </>
              ) : (
                <p className="text-sm text-red-600">{result.error}</p>
              )}
            </div>
          )}
        </div>

        {/* Recent logs sidebar */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 h-fit">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Tests Recientes</h3>
          {logs.length === 0 ? (
            <p className="text-xs text-[#94A3B8] text-center py-4">Sin tests aún</p>
          ) : (
            <div className="space-y-2">
              {logs.map(l => (
                <div key={l.id} className="border-b border-[#F1F5F9] pb-2 last:border-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-medium text-[#0F172A]">{l.model_key?.split(':')[1] || l.model_key}</span>
                    {l.status === 'success' ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : <AlertCircle className="w-3 h-3 text-red-400" />}
                  </div>
                  <p className="text-[10px] text-[#94A3B8] truncate">{l.prompt_preview}</p>
                  <div className="flex gap-3 text-[10px] text-[#CBD5E1] mt-0.5">
                    <span>{l.total_tokens}tok</span>
                    <span>{l.latency_ms}ms</span>
                    <span>{l.created_at ? new Date(l.created_at).toLocaleTimeString() : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
